export interface SearchHit<T> {
  item: T;
  score: number;
  ranges: Array<[number, number]>;
}

interface Normalized {
  norm: string;
  map: number[];
}

function normalize(s: string): Normalized {
  const chars: string[] = [];
  const map: number[] = [];
  for (let i = 0; i < s.length; i++) {
    const ch = s[i].toLowerCase();
    if (/[\p{L}\p{N}]/u.test(ch)) {
      chars.push(ch);
      map.push(i);
    }
  }
  return { norm: chars.join(""), map };
}

function spanFromNorm(map: number[], start: number, end: number): [number, number] {
  return [map[start], map[end - 1] + 1];
}

function runsFromNorm(map: number[], positions: number[]): Array<[number, number]> {
  const runs: Array<[number, number]> = [];
  for (const p of positions) {
    const at = map[p];
    const last = runs[runs.length - 1];
    if (last && last[1] === at) last[1] = at + 1;
    else runs.push([at, at + 1]);
  }
  return runs;
}

function mergeRanges(ranges: Array<[number, number]>): Array<[number, number]> {
  if (ranges.length < 2) return ranges;
  const sorted = [...ranges].sort((a, b) => a[0] - b[0]);
  const out: Array<[number, number]> = [sorted[0]];
  for (const [s, e] of sorted.slice(1)) {
    const last = out[out.length - 1];
    if (s <= last[1]) last[1] = Math.max(last[1], e);
    else out.push([s, e]);
  }
  return out;
}

// MIN_FUZZY_DENSITY stops a query from scattering its letters across an unrelated title.
const MIN_FUZZY_QUERY_LEN = 4;
const MIN_FUZZY_DENSITY = 0.5;

function subsequence(norm: string, q: string): { positions: number[]; gaps: number } | null {
  const positions: number[] = [];
  let gaps = 0;
  let at = 0;
  for (const ch of q) {
    const found = norm.indexOf(ch, at);
    if (found === -1) return null;
    if (positions.length && found > at) gaps += found - at;
    positions.push(found);
    at = found + 1;
  }
  return { positions, gaps };
}

function coverage(queryLen: number, titleLen: number): number {
  if (titleLen === 0) return 0;
  return Math.round((100 * queryLen) / titleLen);
}

// Bands are disjoint: token penalty caps at 199 NOT 299 so its floor stays above fuzzy. Keep it.
function scoreTitle(
  norm: string,
  map: number[],
  q: string,
  tokens: string[],
): Omit<SearchHit<unknown>, "item"> | null {
  const cover = coverage(q.length, norm.length);

  const idx = norm.indexOf(q);
  if (idx !== -1) {
    const startsTitle = idx === 0 ? 200 : 0;
    return {
      score: 1000 + startsTitle - Math.min(idx, 199) + cover,
      ranges: [spanFromNorm(map, idx, idx + q.length)],
    };
  }

  if (tokens.length > 1) {
    const ranges: Array<[number, number]> = [];
    let total = 0;
    let matchedAll = true;
    for (const token of tokens) {
      const i = norm.indexOf(token);
      if (i === -1) {
        matchedAll = false;
        break;
      }
      total += i;
      ranges.push(spanFromNorm(map, i, i + token.length));
    }
    if (matchedAll) return { score: 600 - Math.min(total, 199) + cover, ranges: mergeRanges(ranges) };
  }

  if (q.length >= MIN_FUZZY_QUERY_LEN) {
    const sub = subsequence(norm, q);
    if (sub) {
      const first = sub.positions[0];
      const last = sub.positions[sub.positions.length - 1];
      const density = q.length / (last - first + 1);
      if (density >= MIN_FUZZY_DENSITY) {
        return { score: 300 - Math.min(sub.gaps, 299) + cover, ranges: runsFromNorm(map, sub.positions) };
      }
    }
  }

  return null;
}

export function searchConversations<T extends { title: string | null }>(
  items: T[],
  query: string,
): Array<SearchHit<T>> {
  const q = normalize(query).norm;
  if (!q) return items.map((item) => ({ item, score: 0, ranges: [] }));

  const tokens = query
    .split(/\s+/)
    .map((t) => normalize(t).norm)
    .filter(Boolean);

  const hits: Array<SearchHit<T>> = [];
  for (const item of items) {
    const title = item.title ?? "";
    const { norm, map } = normalize(title);
    if (!norm) continue;
    const scored = scoreTitle(norm, map, q, tokens);
    if (scored) hits.push({ item, score: scored.score, ranges: scored.ranges });
  }

  return hits.sort((a, b) => b.score - a.score);
}
